export interface DeviceTypeResult {
  isMobile: boolean;
  isPc: boolean;
  rootFontSize: number;
}

const CACHE_KEY = "viewport_cache";

function getCachedViewport(): { shortSide: number; longSide: number; stableWidth: number } | null {
  const raw = sessionStorage.getItem(CACHE_KEY);
  return raw ? JSON.parse(raw) : null;
}

function setCachedViewport(innerWidth: number, innerHeight: number) {
  const shortSide = Math.min(innerWidth, innerHeight);
  const longSide = Math.max(innerWidth, innerHeight);
  const stableWidth = innerWidth;
  const cached = { shortSide, longSide, stableWidth };
  sessionStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  return cached;
}

function isPossiblyVirtualKeyboardVisible(
  innerWidth: number,
  innerHeight: number,
  cachedWidth: number,
  cachedHeight: number
): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const isTouch = navigator.maxTouchPoints > 0;
  const isMobileUA = /android|iphone|ipad|mobile/.test(ua);

  const WIDTH_THRESHOLD = 2;
  const HEIGHT_DROP_PX = 100;
  const SHRINK_RATIO = 0.85;

  const vv = window.visualViewport;
  const visualKeyboard =
    vv &&
    cachedHeight - vv.height > HEIGHT_DROP_PX &&
    vv.height / cachedHeight < SHRINK_RATIO &&
    Math.abs(vv.width - cachedWidth) <= WIDTH_THRESHOLD;

  const widthDiff = Math.abs(innerWidth - cachedWidth);
  const fallbackKeyboard = widthDiff <= WIDTH_THRESHOLD && innerHeight < cachedHeight;

  return isTouch && isMobileUA && (visualKeyboard || fallbackKeyboard);
}

function maybeUpdateCachedViewport(
  innerWidth: number,
  innerHeight: number,
  cachedWidth: number,
  cachedHeight: number
) {
  if (!isPossiblyVirtualKeyboardVisible(innerWidth, innerHeight, cachedWidth, cachedHeight)) {
    setCachedViewport(innerWidth, innerHeight);
  }
}

export default function detectType(): DeviceTypeResult {
  const innerWidth = window.innerWidth;
  const innerHeight = window.innerHeight;

  const cached = getCachedViewport();
  if (!cached) {
    setCachedViewport(innerWidth, innerHeight);
  } else {
    maybeUpdateCachedViewport(innerWidth, innerHeight, cached.stableWidth, cached.longSide);
  }

  const { shortSide, longSide, stableWidth } = getCachedViewport()!;

  const isMobile = stableWidth < 1025;
  const isPc = !isMobile;

  let rootFontSize = 16;
  if (isMobile) {
    rootFontSize =
      shortSide < 650 && longSide / shortSide > 1.45
        ? parseFloat((shortSide / 10).toFixed(1))
        : parseFloat((shortSide / 20).toFixed(1));
    rootFontSize = Math.min(60, Math.max(37.5, rootFontSize));
  }

  return { isMobile, isPc, rootFontSize };
}
